import React, { useMemo, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  Select,
  SelectWithColor
} from 'api/sharedComponents/reactHookFormComponents';
import { yupResolver } from '@hookform/resolvers/yup';
import { EDIT_AVAILABILITY_SET_SHIFT_PROVIDER } from 'utils/validators/availabilityTemplate/editAvailabilityBlock';
import { mapToLocationsDownOption } from 'utils/mappers/form';
import { IClinic } from 'model/v2/clinic';
import { ILocation } from 'model/v2/location';
import ClinicSelect from './ClinicSelect';
import ModalBottomWithDelete from '../ReusableComponents/ModalBottomWithDelete';
import { setShiftData } from 'model/v2/setShiftBlock';
import moment from 'moment';
import { CalenderEvent, SchedulingBlock } from 'model/v2/availabilityTemplate';
import FormItem from 'api/sharedComponents/reactHookFormComponents/formItem';
import { checkForBlockOverlaps } from './overlapUtils';
import { Alert } from '@material-ui/lab';
import { Typography } from '@material-ui/core';
import { ProviderSetShiftWrapper } from '../style';

interface IProps {
  onClose(): void;
  onSubmit(data: any, oldEvent: CalenderEvent): void;
  onDelete(selectedEvent: CalenderEvent): void;
  data: any;
  clinics?: IClinic[];
  providerId?: number;
  providerDefaultClinicsIds?: number[];
  locations?: ILocation[];
  providerSetShift: setShiftData[];
  hasSetShift: boolean;
  existingEvents?: CalenderEvent[]; // Add this prop for overlap checking
  selectedDate?: Date;
}

interface ITemplateForm {
  isAdditionalShift: boolean;
  block: string;
  location: number;
  clinics: number[];
  recurringDays: number[];
}

const SetShiftForm: React.FC<IProps> = ({
  onClose,
  onSubmit,
  onDelete,
  data,
  clinics,
  providerId,
  locations,
  providerDefaultClinicsIds,
  providerSetShift,
  // hasSetShift,
  existingEvents = [], // Add default value
  selectedDate
}) => {
  const [overlapWarning, setOverlapWarning] = useState(false);

  // Use a ref to always get the most current existingEvents
  const existingEventsRef = React.useRef(existingEvents);
  existingEventsRef.current = existingEvents;
  const locationsOptions = useMemo(() => {
    return mapToLocationsDownOption(locations || []);
  }, [locations]);

  const defaultClinics = useMemo(() => {
    return data.clinics && data.clinics.length !== 0
      ? data.clinics.map((clinicId: { id: number }) => clinicId.id)
      : providerDefaultClinicsIds || [];
  }, [data.clinics, providerDefaultClinicsIds]);

  const methods = useForm<ITemplateForm>({
    defaultValues: {
      isAdditionalShift: data.type === 'Additional Availability' ? true : false, // Set based on data type
      location: data.location || locations?.[0]?.id || 0,
      clinics: defaultClinics,
      block:
        data.schedulingShiftId ||
        data.block ||
        data.schedulingShiftId?.toString() ||
        ''
    },
    resolver: yupResolver(EDIT_AVAILABILITY_SET_SHIFT_PROVIDER)
  });

  useEffect(() => {
    if (data.schedulingShiftId) {
      methods.setValue('block', data.schedulingShiftId);
    } else {
      methods.setValue('block', providerSetShift[0]?.shiftId || '');
    }

    const hasExistingBlockAvailability = existingEvents.some(
      event =>
        (event.type === 'working' &&
          event.schedulingShiftId !== null &&
          event.schedulingShiftId !== undefined &&
          event.schedulingShiftId > 0 &&
          !event.isAdditionalShift) ||
        event.type === 'Block Availability'
    );

    if (data.type === 'Block Availability' || data.type === 'working') {
      methods.setValue('isAdditionalShift', false);
    } else if (data.type === 'Additional Availability') {
      methods.setValue('isAdditionalShift', true);
    } else if (hasExistingBlockAvailability) {
      methods.setValue('isAdditionalShift', true);
    } else {
      methods.setValue('isAdditionalShift', false);
    }
  }, [data.schedulingShiftId, data.type, existingEvents, providerSetShift]);

  const formatShiftBlockToEvent = (shiftData: any, providerSetShift: any[]) => {
    const { location, clinics, block, isAdditionalShift } = shiftData;

    const calendarEvents: CalenderEvent[] = [];
    const shiftInfo = providerSetShift.find(
      shift => shift.shiftId === Number(block)
    );
    if (!shiftInfo) return [];

    if (!shiftInfo) return [];
    shiftInfo.blocks.forEach((block: SchedulingBlock) => {
      const startMoment = moment(block.startTime, 'HH:mm');
      const endMoment = moment(block.endTime, 'HH:mm');
      const days = block.days;
      days.forEach((day: number) => {
        const event: CalenderEvent = {
          title: isAdditionalShift
            ? 'Additional Availability'
            : 'Block Availability',
          type: isAdditionalShift ? 'Additional Availability' : 'working',
          location: location,
          start: moment()
            .day(day)
            .hour(startMoment.hour())
            .minute(startMoment.minute())
            .toDate(),
          end: moment()
            .day(day)
            .hour(endMoment.hour())
            .minute(endMoment.minute())
            .toDate(),
          clinics: clinics,
          isAdmin: true,
          block: block.id.toString(),
          schedulingShiftId: Number(shiftInfo.shiftId),
          isAdditionalShift: isAdditionalShift
        };

        calendarEvents.push(event);
      });
    });

    return calendarEvents;
  };

  const submitForm = React.useCallback(
    (submittedData: any) => {
      setOverlapWarning(false);

      const currentExistingEvents = existingEventsRef.current;

      if (currentExistingEvents.length === 0) {
        const formattedData = formatShiftBlockToEvent(
          submittedData,
          providerSetShift
        );
        onSubmit(formattedData, data);
        return;
      }

      const isNewBlock = !data.id && !data.schedulingShiftId;
      let excludeEventId: string | number | undefined;
      let excludeSchedulingShiftId: number | undefined;

      if (!isNewBlock) {
        if (data.schedulingShiftId) {
          excludeSchedulingShiftId = data.schedulingShiftId;
        } else if (data.id) {
          excludeEventId = data.id;
        }
      }

      const freshExistingEvents = currentExistingEvents;

      let hasOverlaps = false;
      try {
        hasOverlaps = checkForBlockOverlaps(
          submittedData,
          providerSetShift,
          freshExistingEvents,
          selectedDate!,
          excludeEventId,
          excludeSchedulingShiftId
        );
      } catch (error) {
        console.error('Error in overlap check:', error);
        hasOverlaps = false;
      }

      if (hasOverlaps) {
        setOverlapWarning(true);
        return;
      }

      const formattedData = formatShiftBlockToEvent(
        submittedData,
        providerSetShift
      );
      onSubmit(formattedData, data);
    },
    [onSubmit, data, providerSetShift, selectedDate]
  );
  return (
    <FormProvider {...methods}>
      <ProviderSetShiftWrapper>
        <FormItem label="Is this additional hours?">
          <Controller
            name="isAdditionalShift"
            control={methods.control}
            render={fieldProps => (
              <input
                type="checkbox"
                checked={fieldProps.value}
                onChange={e => fieldProps.onChange(e.target.checked)}
                disabled={
                  data.type === 'Additional Availability' ||
                  existingEvents.some(
                    event =>
                      (event.type === 'working' &&
                        event.schedulingShiftId !== null &&
                        event.schedulingShiftId !== undefined &&
                        event.schedulingShiftId > 0 &&
                        !event.isAdditionalShift) ||
                      event.type === 'Block Availability'
                  )
                }
              />
            )}
          />
        </FormItem>
        {overlapWarning && (
          <Alert
            severity="warning"
            onClose={() => setOverlapWarning(false)}
            style={{
              background: '#fff3cd',
              color: '#856404',
              marginBottom: '20px'
            }}
          >
            <Typography style={{ color: '#856404' }}>
              The selected Block conflicts with existing events. Please choose a
              different Block or time.
            </Typography>
          </Alert>
        )}

        <FormItem label="Blocks">
          <Controller
            name="block"
            control={methods.control}
            render={fieldProps => {
              return (
                <Select
                  options={providerSetShift.map(block => ({
                    value: block.shiftId,
                    label: block.description
                  }))}
                  field={fieldProps}
                  errors={methods.formState.errors}
                  value={fieldProps.value}
                />
              );
            }}
          />
        </FormItem>

        <FormItem label="Location">
          <Controller
            name="location"
            control={methods.control}
            render={fieldProps => (
              <SelectWithColor
                options={locationsOptions}
                field={fieldProps}
                errors={methods.errors}
              />
            )}
          />
        </FormItem>

        <FormItem label="Clinics">
          <Controller
            name="clinics"
            control={methods.control}
            defaultValue={defaultClinics}
            render={() => (
              <ClinicSelect
                providerId={providerId}
                clinics={clinics!}
                defaultData={defaultClinics}
              />
            )}
          />
        </FormItem>
      </ProviderSetShiftWrapper>

      <ModalBottomWithDelete
        onClose={onClose}
        submitForm={methods.handleSubmit(submitForm)}
        onDelete={onDelete}
        selectedEvent={data}
      />
    </FormProvider>
  );
};

export default SetShiftForm;
